import { Application } from '@feathersjs/feathers';
import { AuthenticationClientOptions } from '@feathersjs/authentication-client';
import decodeJwt from 'jwt-decode';

function authClient(
  client: Application,
  options: Partial<AuthenticationClientOptions> = {}
) {
  const {
    storageKey,
    authenticate,
    permissionsKey,
    permissionsField,
    passwordField,
    usernameField,
    redirectTo,
    logoutOnForbidden,
    userKey
  } = {
    storageKey: 'token',
    authenticate: { strategy: 'local' },
    permissionsKey: 'permissions',
    permissionsField: 'roles',
    passwordField: 'password',
    usernameField: 'email',
    logoutOnForbidden: true,
    userKey: 'user',
    redirectTo: '/login',
    ...options
  };

  return {
    login(data: any) {
      const { username, password } = data;
      return client
        .authenticate({
          ...authenticate,
          [usernameField]: username,
          [passwordField]: password
        })
        .then(({ user }) => {
          const permissions = ['admin'];

          if (!permissions.length) {
            localStorage.removeItem(storageKey);
            throw new Error('Você não é um administrador.');
          }

          user.id = user._id;
          user.fullName = user.name;

          localStorage.setItem(permissionsKey, JSON.stringify(permissions));
          localStorage.setItem(userKey, JSON.stringify(user));
        });
    },
    logout() {
      return client.logout().then(() => {
        localStorage.removeItem(permissionsKey);
        localStorage.removeItem(userKey);
      });
    },
    checkAuth() {
      const hasJwtInStorage = !!localStorage.getItem(storageKey);
      const hasReAuthenticate =
        Object.getOwnPropertyNames(client).includes('reAuthenticate') &&
        typeof client.reAuthenticate === 'function';

      if (hasJwtInStorage && hasReAuthenticate) {
        return client
          .reAuthenticate()
          .then(() => Promise.resolve())
          .catch(() => Promise.reject({ redirectTo }));
      }

      return hasJwtInStorage
        ? Promise.resolve()
        : Promise.reject({ redirectTo });
    },
    checkError(error: any) {
      const { code } = error;
      if (code === 401 || (logoutOnForbidden && code === 403)) {
        localStorage.removeItem(storageKey);
        localStorage.removeItem(permissionsKey);
        return Promise.reject();
      }
      return Promise.resolve();
    },
    async getPermissions() {
      /*
      JWT token may be provided by oauth,
      so that's why the permissions are decoded here and not in AUTH_LOGIN.
      */
      // Get the permissions from localstorage if any.
      const permissionsJSON = localStorage.getItem(permissionsKey);

      if (permissionsJSON) {
        const localStoragePermissions = JSON.parse(permissionsJSON);
        // If any, provide them.
        if (localStoragePermissions) {
          return localStoragePermissions;
        }
      }

      // Or find them from the token, save them and provide them.
      try {
        const jwtToken = localStorage.getItem(storageKey);
        if (!jwtToken) {
          throw new Error('No token in local storage.');
        }
        const decodedToken = decodeJwt(jwtToken) as any;
        const jwtPermissions = decodedToken[permissionsField] || [];
        localStorage.setItem(permissionsKey, JSON.stringify(jwtPermissions));
        return jwtPermissions;
      } catch (e) {
        return Promise.reject();
      }
    },
    async getIdentity() {
      // Get the user from localstorage if set.
      const userJSON = localStorage.getItem(userKey);
      if (userJSON) {
        const localStorageUser = JSON.parse(userJSON);
        // If any, provide them.
        if (localStorageUser) {
          return localStorageUser;
        }
      }

      // Or find them from the token, save them and provide them.
      try {
        const jwtToken = localStorage.getItem(storageKey);

        if (jwtToken) {
          const decodedToken = decodeJwt(jwtToken) as any;

          const saveUser = (user: any) => {
            if (user) {
              user.id = user._id;
              user.fullName = user.name;
              localStorage.setItem(userKey, JSON.stringify(user));
              return user;
            } else {
              return Promise.reject();
            }
          };

          await this.getPermissions();
          const user = await client.service('users').get(decodedToken['sub']);
          return saveUser(user);
        }
      } catch (e) {
        return Promise.reject();
      }
    }
  };
}

export default function authProvider(app: Application) {
  return authClient(app, { storageKey: 'feathers-jwt' });
}
